import {
  isForegroundPageFailure,
  queryOperationName,
  type QueryLike,
} from "@shared/queryForegroundPolicy";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useSyncExternalStore } from "react";

export function useForegroundPageFailure(): { error: unknown; operation: string } | null {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryCache();
  const subscribe = useCallback(
    (onStoreChange: () => void) => cache.subscribe(onStoreChange),
    [cache],
  );
  const getSnapshot = useCallback(() => foregroundPageFailureFingerprint(cache.getAll()), [cache]);
  const failureFingerprint = useSyncExternalStore(subscribe, getSnapshot, () => "");

  return useMemo(() => {
    if (!failureFingerprint) return null;
    const failed = cache
      .getAll()
      .map((query) => query as unknown as QueryLike)
      .find(isForegroundPageFailure);
    if (!failed) return null;
    return { error: failed.state.error, operation: queryOperationName(failed) };
  }, [cache, failureFingerprint]);
}

/**
 * Stable external-store snapshot for blocking query failures.
 *
 * QueryCache emits observer lifecycle notifications while components render.
 * Returning a primitive fingerprint lets React ignore those notifications unless
 * the actual foreground failure changes, preventing a notify -> setState -> render
 * feedback loop.
 */
export function foregroundPageFailureFingerprint(queries: readonly unknown[]): string {
  const failed = queries
    .map((query) => query as QueryLike)
    .find(isForegroundPageFailure);
  if (!failed) return "";
  const hash = String(failed.queryHash ?? safeQueryKey(failed.queryKey));
  const error = failed.state.error as
    | { name?: unknown; message?: unknown; data?: { code?: unknown; httpStatus?: unknown } }
    | null
    | undefined;
  return JSON.stringify([
    hash,
    queryOperationName(failed),
    failed.state.status,
    failed.state.fetchStatus,
    String(error?.name ?? ""),
    String(error?.message ?? ""),
    String(error?.data?.code ?? ""),
    String(error?.data?.httpStatus ?? ""),
  ]);
}

function safeQueryKey(value: unknown): string {
  try {
    return JSON.stringify(value ?? "");
  } catch {
    return String(value ?? "");
  }
}
